import SearchIcon from '@mui/icons-material/Search'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useState } from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import DateRangePicker from '../../../../../components/DateRangePicker'
import ExportExcel from '../../../../../components/Export'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../../../components/SelectSearch/ChannelSelectSearch'
import ProductGroupSelectSearch from '../../../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import {
  ImageLinks,
  LogoTypeProvider,
} from '../../../../../constants/image-link'
import useAxios from '../../../../../hooks/useAxios'
import { useUser } from '../../../../../hooks/useUser'
import { ICategory } from '../../../../../types/category'
import { IDistributionChannel } from '../../../../../types/distribution-channel'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IProduct } from '../../../../../types/product'
import { AllowShippingProviderLists } from '../../../../../utils/provider'
import { EStatusOrderShowTracking } from '../../../../Order/components/OrderFilterDrawer'

export interface IFilterMapped {
  categoryObjectIds?: string[]
  distributionObjectIds?: string[]
  productObjectIds?: string[]
  productGroups?: string[]
  status?: string
  shippingCostDisplay?: string
  calTax?: string
  startDate?: string
  endDate?: string
  commissionType?: string
  trackingStatus?: string
  expense?: number
}

interface FilterGroupProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  onDownload?: (date: Dayjs) => void
}

const menuItemsTypeOrderType = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'รายการทั่วไป',
    value: 'order',
  },
  {
    label: 'รายการจาก CRM',
    value: 'crm',
  },
  {
    label: 'รายการจาก Telesale',
    value: 'telesale',
  },
]

const menuItemsShippingCostDisplayType = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'แสดง',
    value: '1',
  },
  {
    label: 'ไม่แสดง',
    value: '0',
  },
]

const menuItemsCalTaxType = [
  {
    label: 'ไม่คำนวนภาษีและยอดตีกลับ',
    value: '0',
  },
  {
    label: 'คำนวนภาษีและยอดตีกลับ',
    value: '1',
  },
]

const menuItemsTypeStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'ส่งออกแล้ว',
    value: EStatusOrderShowTracking.SHIPPED,
  },
  {
    label: 'เฉพาะรายการที่มี Tracking',
    value: EStatusOrderShowTracking.TRACKING,
  },
  {
    label: 'ยังไม่มี Tracking',
    value: EStatusOrderShowTracking.NO_TRACKING,
  },
  {
    label: 'สินค้าค้างส่ง',
    value: EStatusOrderShowTracking.PENDING,
  },
]

const DailyTelesalesReportFilter = (props: FilterGroupProps) => {
  const { filter, onFilterChange, onSearch, onDownload } = props
  const {
    startDate,
    endDate,
    productObjectIds,
    distributionObjectIds,
    categoryObjectIds,
    expense = 1,
  } = filter
  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')
  const { merchant } = useUser()

  const productsList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      search: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    try {
      await distributionChannelsList.mutate()
    } catch (error) {}
  }

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            onFilterChange({ ...filter, startDate, endDate })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={8 / 3}>
        <ProductSelectSearch
          resetAutoComplete={resetAutoComplete}
          products={productObjectIds}
          handleFilterChange={handleFilterChange}
          onSelect={(e) => {
            const selectedProductIds = e?.map(
              (product: any) => product?.value?.objectId,
            )
            handleFilterChange({ productObjectIds: selectedProductIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={8 / 3}>
        <ProductGroupSelectSearch
          resetAutoComplete={resetAutoComplete}
          productGroups={filter.productGroups}
          onSelect={(e) => {
            const selectedGroupIds = e?.map((group) => group.value.objectId)
            handleFilterChange({ productGroups: selectedGroupIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={8 / 3}>
        <CategorySelectSearch
          resetAutoComplete={resetAutoComplete}
          categories={categoryObjectIds}
          onSelect={(e) => {
            const selectedCategoryIds = e?.map(
              (category: any) => category?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              categoryObjectIds: selectedCategoryIds,
            })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <ChannelSelectSearch
          resetAutoComplete={resetAutoComplete}
          label={'เลือกช่องทางการขาย'}
          onSelect={(e) => {
            const selectedChannelIds = e?.map(
              (channel: any) => channel?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              distributionObjectIds: selectedChannelIds,
            })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <TextField
          key={resetAutoComplete}
          sx={{ minWidth: 200 }}
          fullWidth
          label="เลือกประเภทค่าคอม"
          select
          onChange={(e) =>
            onFilterChange({
              ...filter,
              commissionType: e.target.value as string,
            })
          }
        >
          <MenuItem value={'all'}>ไม่เลือก</MenuItem>
          <MenuItem value={'commission'}>ค่าคอมต่อชิ้น</MenuItem>
          <MenuItem value={'commissionOrder'}>ค่าคอมต่อคำสั่งซื้อ</MenuItem>
          <MenuItem value={'commissionPercent'}>ค่าคอม %</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} lg={2}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="shippingCostDisplay-label">เลือกขนส่ง</InputLabel>
          <Select
            key={resetAutoComplete}
            id="shippingCostDisplay"
            name="shippingCostDisplay"
            labelId="shippingCostDisplay-label"
            defaultValue={filter.shippingCostDisplay ?? undefined}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                shippingCostDisplay: e.target.value as string,
              })
            }
            fullWidth
          >
            <MenuItem disabled value="" selected>
              <em>การแสดงผลค่าขนส่ง</em>
            </MenuItem>
            <MenuItem value="all" key="all">
              ทั้งหมด
            </MenuItem>
            {AllowShippingProviderLists(merchant).map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.value !== 'all' && (
                  <img
                    style={{ marginRight: 5 }}
                    src={
                      ImageLinks[
                        `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                      ]
                    }
                    alt="smallLogoSocial"
                    height={20}
                  />
                )}
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={2}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => {
            onFilterChange({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              status: undefined,
              shippingCostDisplay: undefined,
              calTax: undefined,
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            onSearch({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              status: undefined,
              shippingCostDisplay: undefined,
              calTax: undefined,
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            setResetAutoComplete(new Date().getTime().toString())
          }}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
      <Grid item xs={12} lg={2}>
        <ExportExcel onDownload={onDownload} filterDate={false} />
      </Grid>
    </Grid>
  )
}

export default DailyTelesalesReportFilter
